export type FinnishHolidayType = "public" | "other";

export interface FinnishHoliday {
    date: string; // YYYY-MM-DD in local time
    name: string;
    type: FinnishHolidayType;
}

// Cache holidays per year so we don't recompute them on every render
const holidaysByYear: { [year: number]: Record<string, FinnishHoliday> } = {};

/**
 * Normalize a Date to a YYYY-MM-DD string using local time.
 */
const toKey = (date: Date): string => {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, "0");
    const day = `${date.getDate()}`.padStart(2, "0");
    return `${year}-${month}-${day}`;
};

/**
 * Compute Easter Sunday for a given year using the Meeus/Jones/Butcher algorithm.
 */
const getEasterSunday = (year: number): Date => {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31); // 3=March, 4=April
    const day = ((h + l - 7 * m + 114) % 31) + 1;
    // Use local time; we only care about the date part
    return new Date(year, month - 1, day);
};

const addDays = (date: Date, days: number): Date => {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    return d;
};

/**
 * Find Midsummer Eve and Midsummer Day in Finland:
 * - Midsummer Eve: Friday between 19–25 June
 * - Midsummer Day: Saturday between 20–26 June (day after Midsummer Eve)
 */
const getMidsummerHolidays = (year: number): { eve: Date; day: Date } => {
    const start = new Date(year, 5, 19); // June is month 5 (0-based)
    let midsummerEve = new Date(start);
    // Find first Friday on or after June 19
    while (midsummerEve.getDay() !== 5) {
        midsummerEve.setDate(midsummerEve.getDate() + 1);
    }
    const midsummerDay = addDays(midsummerEve, 1);
    return { eve: midsummerEve, day: midsummerDay };
};

/**
 * All Saints' Day in Finland:
 * - Saturday between 31 October and 6 November.
 */
const getAllSaintsDay = (year: number): Date => {
    const start = new Date(year, 9, 31); // 31 October
    let allSaints = new Date(start);
    while (allSaints.getDay() !== 6 && allSaints.getMonth() <= 10 && allSaints.getDate() <= 6) {
        allSaints.setDate(allSaints.getDate() + 1);
    }
    return allSaints;
};

/**
 * Build all standard Finnish public holidays for a given year.
 * List is not exhaustive of every possible observance, but covers the main red-letter days.
 */
const buildFinnishHolidaysForYear = (year: number): Record<string, FinnishHoliday> => {
    const map: Record<string, FinnishHoliday> = {};

    const addHoliday = (date: Date, name: string, type: FinnishHolidayType = "public") => {
        const key = toKey(date);
        map[key] = { date: key, name, type };
    };

    // Fixed-date holidays
    addHoliday(new Date(year, 0, 1), "Uudenvuodenpäivä"); // New Year's Day
    addHoliday(new Date(year, 0, 6), "Loppiainen"); // Epiphany
    addHoliday(new Date(year, 4, 1), "Vappu"); // May Day (1 May)
    addHoliday(new Date(year, 11, 6), "Itsenäisyyspäivä"); // Independence Day (6 December)
    addHoliday(new Date(year, 11, 24), "Jouluaatto"); // Christmas Eve
    addHoliday(new Date(year, 11, 25), "Joulupäivä"); // Christmas Day
    addHoliday(new Date(year, 11, 26), "Tapaninpäivä"); // Boxing Day

    // Easter-related holidays
    const easterSunday = getEasterSunday(year);
    const goodFriday = addDays(easterSunday, -2);
    const easterMonday = addDays(easterSunday, 1);
    const ascensionDay = addDays(easterSunday, 39);
    const pentecostSunday = addDays(easterSunday, 49);

    addHoliday(goodFriday, "Pitkäperjantai");
    addHoliday(easterSunday, "Pääsiäispäivä");
    addHoliday(easterMonday, "2. pääsiäispäivä");
    addHoliday(ascensionDay, "Helatorstai");
    addHoliday(pentecostSunday, "Helluntaipäivä");

    // Midsummer
    const midsummer = getMidsummerHolidays(year);
    addHoliday(midsummer.eve, "Juhannusaatto");
    addHoliday(midsummer.day, "Juhannuspäivä");

    // All Saints' Day
    const allSaintsDay = getAllSaintsDay(year);
    addHoliday(allSaintsDay, "Pyhäinpäivä");

    return map;
};

const getYearMap = (year: number): Record<string, FinnishHoliday> => {
    if (!holidaysByYear[year]) {
        holidaysByYear[year] = buildFinnishHolidaysForYear(year);
    }
    return holidaysByYear[year];
};

/**
 * Get Finnish holiday information for a specific Date (local time).
 * Returns null if the date is not a Finnish holiday.
 */
export const getFinnishHoliday = (date: Date): FinnishHoliday | null => {
    const year = date.getFullYear();
    const key = toKey(date);
    const yearMap = getYearMap(year);
    return yearMap[key] ?? null;
};

